import { HttpClient } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { Environment, EnvironmentService } from '@galaxy/core';
import { App, AppKey, AppPartnerService, AppPricingService, FieldMask, LmiCategory } from '@galaxy/marketplace-apps';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { Currency, Frequency, LineItem, Package, PackageService, Status } from '@vendasta/marketplace-packages';
import { File, PackageDetails, Salesperson, StoreLineItem } from '@vendasta/store';
import { Breadcrumbs } from '@vendasta/uikit';
import { BehaviorSubject, Observable, Subscription, combineLatest, of } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  map,
  pluck,
  publishReplay,
  refCount,
  shareReplay,
  switchMap,
} from 'rxjs/operators';
import { InIFrame } from '../common';
import { InterestInPackageDialogComponent } from '../interest-in-package-dialog/interest-in-package-dialog.component';
import { FeatureFlagService } from '../services/featureFlag.service';
import { sumPrices } from '../util/pricing';
import { buildStoreRedirectURLForPackage } from '../util/store';
import { AppPrice } from '@vendasta/marketplace-apps';

@Component({
  selector: 'strfrnt-package-view',
  templateUrl: './package-view.component.html',
  styleUrls: ['./package-view.component.scss'],
  standalone: false,
})
export class PackageViewComponent implements OnInit, OnDestroy {
  labelContact = 'Contact Sales';
  labelBuyItNow = 'Buy';
  labelExternalURL = 'Buy';
  public breadcrumbs$: Observable<Breadcrumbs[]>;

  public salesperson$: Observable<Salesperson>;
  public package$: Observable<Package>;
  public storePackage$: Observable<PackageDetails>;

  subscriptions: Subscription = new Subscription();
  marketId$: Observable<string>;
  partnerId$: Observable<string>;
  apps$: Observable<App[]>;
  selfSignupEnabled$: Observable<boolean>;
  selfSignupEnabled$$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  getItNowflag$: Observable<boolean>;
  usesExternalUrl: boolean;
  appPrice$: Observable<AppPrice>;

  constructor(
    private packageService: PackageService,
    private route: ActivatedRoute,
    private alertService: SnackbarService,
    private appPartnerService: AppPartnerService,
    private dialog: MatDialog,
    private environmentService: EnvironmentService,
    private http: HttpClient,
    private featureService: FeatureFlagService,
    private appPricingService: AppPricingService,
  ) {}

  private initializeObservables(): void {
    this.partnerId$ = this.route.paramMap.pipe(map((paramMap) => paramMap.get('partnerId')));
    this.marketId$ = this.route.paramMap.pipe(map((paramMap) => paramMap.get('marketId')));
  }

  ngOnInit(): void {
    this.initializeObservables();

    const rawPackage$ = this.route.params.pipe(
      pluck('packageId'),
      distinctUntilChanged(),
      switchMap((pkgId: string) => {
        return this.packageService.get(pkgId);
      }),
      catchError(() => {
        this.alertService.openErrorSnack('Failed to get Package details');
        return of(null);
      }),
    );

    this.package$ = combineLatest([rawPackage$, this.partnerId$, this.marketId$]).pipe(
      map(([pkg, partnerId, marketId]) => {
        if (isPackageArchived(pkg)) {
          this.alertService.openErrorSnack('Package not available');
          redirectToMainPage(partnerId, marketId);
        } else {
          return pkg;
        }
      }),
      publishReplay(1),
      refCount(),
    );

    this.appPrice$ = this.package$.pipe(
      distinctUntilChanged(),
      switchMap((pkg) => {
        if (!pkg || !pkg.packageId) {
          return of(null);
        }
        return this.appPricingService
          .getMultiPricing([pkg.packageId], new FieldMask({ paths: ['retail'] }), pkg.partnerId, pkg.marketId)
          .pipe(
            map((appPrices) => {
              if (!appPrices) {
                return null;
              }
              return appPrices[0].pricesForContexts['retail'];
            }),
          );
      }),
    );

    this.apps$ = combineLatest([this.package$, this.marketId$, this.partnerId$]).pipe(
      switchMap(([pkg, marketId, partnerId]) => {
        const appKeys = this.getIDsOfNeededLineItems(pkg);
        return this.appPartnerService.getMulti(appKeys, partnerId, marketId ? marketId : 'default', null);
      }),
    );

    this.selfSignupEnabled$ = combineLatest([this.partnerId$, this.marketId$]).pipe(
      switchMap(([partnerId, marketId]) => {
        return this.featureService.isSMBSelfSignupEnabled$(partnerId, marketId);
      }),
    );

    this.subscriptions.add(
      this.selfSignupEnabled$.subscribe((selfSignupEnabled) => {
        this.selfSignupEnabled$$.next(selfSignupEnabled);
      }),
    );

    this.getItNowflag$ = combineLatest([this.selfSignupEnabled$, this.package$]).pipe(
      map(([selfSignupEnabled, pkg]) => {
        const pckgPrice = sumPrices(pkg?.pricing?.prices);
        return selfSignupEnabled && pkg && pkg.marketAction && pkg.marketAction.orderForm && pckgPrice >= 0;
      }),
    );

    // this.salesperson$ = this.salespersonService.getSalesperson$(
    this.salesperson$ = this.getSalesperson$(
      this.route.params.pipe(map(({ partnerId, marketId }) => ({ partnerId, marketId }))),
    ).pipe(
      catchError((err) => {
        console.error(err);
        this.alertService.openErrorSnack(
          'Oops! Something went wrong showing the contact information for this package!',
        );
        return of(null);
      }),
      shareReplay(1),
    );

    this.storePackage$ = combineLatest([this.package$, this.apps$]).pipe(
      map(([pkg, apps]) => {
        if (pkg.marketAction && pkg.marketAction.externalUrl && pkg.marketAction.externalUrl.url) {
          this.usesExternalUrl = true;
        }
        return this.packageAndAppsToStorePackage(pkg, apps, true);
      }),
      catchError((err) => {
        console.error('Failed to convert store package > ', err);
        this.alertService.openErrorSnack('Failed to get Package details');
        return of(null);
      }),
      publishReplay(1),
      refCount(),
    );

    this.breadcrumbs$ = combineLatest([this.marketId$, this.partnerId$, this.storePackage$]).pipe(
      map(([marketId, partnerId, pkg]) => {
        return [
          { text: 'Store', url: `/public/store/${partnerId}/${marketId || ''}` },
          { text: (pkg && pkg.name) || 'Package', url: undefined },
        ];
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public getIDsOfNeededLineItems(pkg: Package): AppKey[] {
    if (!(pkg && pkg.lineItems && pkg.lineItems.lineItems && pkg.lineItems.lineItems.length)) {
      return [];
    }

    return pkg.lineItems.lineItems.map((li) => {
      return new AppKey({
        appId: li.id,
        editionId: li.editionId,
      });
    });
  }

  public packageAndAppsToStorePackage(pkg: Package, apps: App[], canAccessEditions: boolean): PackageDetails {
    if (!pkg) {
      return null;
    }

    let lineItems: LineItem[] = [];
    if (pkg && pkg.lineItems && pkg.lineItems.lineItems) {
      lineItems = pkg.lineItems.lineItems;
    }

    const appMap = (apps || []).reduce((prev, app) => {
      if (!(app && app.key)) {
        return prev;
      }
      prev.set(app.key.appId, app);
      return prev;
    }, new Map<string, App>());

    const slis: StoreLineItem[] = lineItems.reduce((prev, li) => {
      if (!li) {
        return prev;
      }
      const app = appMap.get(li.id);
      if (!app) {
        return prev;
      }

      const parentDetails = {
        id: '',
        editionId: '',
        icon: '',
        name: '',
        editionName: '',
      };

      if (app.parentRequirements && app.parentRequirements.parentDetails) {
        if (app.parentRequirements.parentDetails.key) {
          parentDetails.id = app.parentRequirements.parentDetails.key.appId;
        }
        parentDetails.icon = app.parentRequirements.parentDetails.iconUrl;
        parentDetails.name = app.parentRequirements.parentDetails.name;

        // TODO: update these fields when editions are supported as application parents
        parentDetails.editionId = '';
        parentDetails.editionName = '';
      }

      const sli = {
        item: {
          id: app.key.appId,
          editionId: app.key.editionId,
          icon: (app.sharedMarketingInformation || { iconUrl: '' }).iconUrl,
          name: (app.sharedMarketingInformation || { name: '' }).name,
          editionName: canAccessEditions ? (app.sharedMarketingInformation || { editionName: '' }).editionName : '',
          description: (app.endUserMarketingInformation || { description: '' }).description,
          keySellingPoints: (app.endUserMarketingInformation || { keySellingPoints: [] }).keySellingPoints,
          faqs: (app.endUserMarketingInformation || { faqs: [] }).faqs,
          screenshotUrls: (app.endUserMarketingInformation || { screenshotUrls: [] }).screenshotUrls,
          files:
            app.endUserMarketingInformation &&
            app.endUserMarketingInformation.fileUrls &&
            app.endUserMarketingInformation.fileUrls.length
              ? app.endUserMarketingInformation.fileUrls.map((url) => new File(url))
              : [],
          lmiCategories: this.convertSDKLmiEnumToDisplayStrings(app.sharedMarketingInformation.lmiCategories),
        },
        parent: parentDetails,
        quantity: li.quantity,
      };

      prev.push(sli);

      return prev;
    }, []);

    const d = new PackageDetails();
    d.name = pkg.name;
    d.solution_id = pkg.packageId;
    d.partner_id = pkg.partnerId;
    d.market_id = pkg.marketId;
    d.icon = pkg.icon;
    d.status = pkg.status ? (isNaN(pkg.status) ? pkg.status.toString() : Status[+pkg.status]) : 'DRAFT';
    d.tagline = pkg.tagline;
    d.header_image_url = pkg.headerImageUrl;

    // TODO check billing bundle if price is not set
    if (!pkg.pricing) {
      d.pricing = {
        currency: 'USD',
        prices: [],
      };
    } else {
      d.pricing = {
        currency: pkg.pricing.currency
          ? isNaN(pkg.pricing.currency)
            ? pkg.pricing.currency.toString()
            : Currency[+pkg.pricing.currency]
          : 'USD',
        prices: pkg.pricing.prices
          ? pkg.pricing.prices.map((p) => {
              return {
                price: p.price || 0,
                isStartingPrice: p.isStartingPrice || false,
                frequency: p.frequency
                  ? isNaN(p.frequency)
                    ? p.frequency.toString()
                    : this.getCapitalizeFirstLetter(Frequency[+p.frequency].toLowerCase())
                  : 'Monthly',
              };
            })
          : [],
      };
    }
    d.content = pkg.content;
    d.line_items = slis;
    d.hide_product_icons_and_names = pkg.hideProductIconsAndNames;
    d.hide_product_details = pkg.hideProductDetails;
    d.market_action = pkg.marketAction;
    return d;
  }

  getCapitalizeFirstLetter(inputString: string): string {
    return inputString.charAt(0).toUpperCase() + inputString.slice(1);
  }

  public convertSDKLmiEnumToDisplayStrings(lmiCats: LmiCategory[]): string[] {
    const sdkLmiEnumToStringMap = new Map<LmiCategory, string>([
      [LmiCategory.LMI_CATEGORY_ADVERTISING, 'Advertising'],
      [LmiCategory.LMI_CATEGORY_CONTENT_AND_EXPERIENCE, 'Content and Experience'],
      [LmiCategory.LMI_CATEGORY_LISTINGS, 'Listings'],
      [LmiCategory.LMI_CATEGORY_REPUTATION, 'Reputation'],
      [LmiCategory.LMI_CATEGORY_SEO, 'SEO'],
      [LmiCategory.LMI_CATEGORY_SOCIAL, 'Social'],
      [LmiCategory.LMI_CATEGORY_WEBSITE, 'Website'],
    ]);

    return (lmiCats || []).map((lmiCat) => sdkLmiEnumToStringMap.get(lmiCat));
  }

  async openDialog(pkg: PackageDetails, salesperson?: Salesperson): Promise<void> {
    const pckgPrice = sumPrices(pkg?.pricing?.prices);
    if (pkg.market_action && pkg.market_action.externalUrl && pkg.market_action.externalUrl.url) {
      window.open(pkg.market_action.externalUrl.url, '_blank');
    } else if (this.selfSignupEnabled$$.value && pkg.market_action && pkg.market_action.orderForm && pckgPrice >= 0) {
      //redirect to business center login page to continue purchase.
      const redirectPath = `/account/location/default/store?p_origin=public-store&partnerId=${pkg.partner_id}&marketId=${pkg.market_id}`;
      if (InIFrame()) {
        window.open(redirectPath);
      } else {
        window.location.href = redirectPath;
      }
    } else {
      const dialogRef = this.dialog.open(InterestInPackageDialogComponent);
      dialogRef.componentInstance.salesPerson = salesperson;
      dialogRef.componentInstance.pkg = pkg;
      await dialogRef.afterClosed().toPromise();
    }
  }

  async addPackageToCart(pkg: PackageDetails): Promise<void> {
    this.alertService.openSuccessSnack(pkg.name + ' has been selected');
    const redirectPath = buildStoreRedirectURLForPackage(pkg.market_id, pkg.solution_id);
    if (InIFrame()) {
      window.open(redirectPath);
    } else {
      window.location.href = redirectPath;
    }
  }

  getSalesperson$(pidAndMid$: Observable<{ partnerId: string; marketId: string }>): Observable<Salesperson> {
    return pidAndMid$.pipe(
      switchMap((params) => this.getSalesperson(params)),
      shareReplay(1),
    );
  }

  async getSalesperson(params: { partnerId: string; marketId: string }): Promise<Salesperson> {
    const url = `${this.host}/_ajax/v1/public-store/salesperson`;
    if (!params.marketId) {
      params.marketId = '';
    }
    const response: any = await this.http.get(url, { params }).toPromise();
    this.provideFormattedName(response.salesperson);
    return response.salesperson;
  }

  private provideFormattedName(salesperson: Salesperson): void {
    if (!salesperson || !!salesperson.formattedName) {
      return;
    }
    const nameParts = [];
    if (salesperson.firstName) {
      nameParts.push(salesperson.firstName);
    }
    if (salesperson.lastName) {
      nameParts.push(salesperson.lastName);
    }
    salesperson.formattedName = nameParts.join(' ');
  }

  private get host(): string {
    switch (this.environmentService.getEnvironment()) {
      case Environment.TEST:
        return 'https://partner-central-test.appspot.com';
      case Environment.DEMO:
        return 'https://partner-central-demo.appspot.com';
      case Environment.PROD:
        return 'https://partners.vendasta.com';
      default:
        return 'https://partner-central-test.appspot.com';
    }
  }
}

function redirectToMainPage(partnerId: string, marketId: string) {
  const redirectPath = `public/store/${partnerId}/${marketId}`;
  if (InIFrame()) {
    window.open(redirectPath);
  } else {
    window.location.href = redirectPath;
  }
}

function isPackageArchived(pkg: Package): boolean {
  return pkg?.status === Status.ARCHIVED;
}
